import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useRangeKey } from '../TimeRangeControl';
import { fetchSources } from '../api';
import ChartTooltip from '../charts/ChartTooltip';
import Leaderboard, { type LeaderboardColumn } from '../charts/Leaderboard';
import type { SourcesPayload } from '../types';
import { colorFor } from '../util';
import { BuiltAt, QUERY_OPTIONS, TabStatus } from './_shared';

type VendorRow = SourcesPayload['vendor_leaderboard'][number];
type LagBucket = SourcesPayload['echo_lag'][number];

const vendorColumns: LeaderboardColumn<VendorRow>[] = [
  {
    key: 'rank',
    label: '#',
    width: '32px',
    align: 'right',
    render: (_row, i) => i + 1,
  },
  {
    key: 'org',
    label: 'Organization',
    render: (row) => row.organization,
  },
  {
    key: 'first',
    label: 'First-mover',
    align: 'right',
    width: '110px',
    render: (row) => row.first_mover_count,
  },
  {
    key: 'total',
    label: 'Total posts',
    align: 'right',
    width: '110px',
    render: (row) => row.total_posts,
  },
  {
    key: 'ratio',
    label: 'Ratio',
    align: 'right',
    width: '70px',
    render: (row) =>
      row.total_posts === 0
        ? '—'
        : `${((row.first_mover_count / row.total_posts) * 100).toFixed(0)}%`,
  },
];

function EchoLagChart({ buckets }: { buckets: LagBucket[] }) {
  const hasData = buckets.some((b) => b.n > 0);
  if (!hasData) {
    return <div className="trends-chart-empty">No echo-lag samples.</div>;
  }
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={buckets} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
        <XAxis dataKey="days_bucket" fontSize={11} />
        <YAxis fontSize={11} />
        <Tooltip content={<ChartTooltip />} />
        <Bar dataKey="n" name="Entities" isAnimationActive={false}>
          {buckets.map((_b, i) => (
            <Cell key={i} fill={colorFor(i)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export default function SourcesTab() {
  const range = useRangeKey();
  const q = useQuery({
    queryKey: ['trends', 'sources', range],
    queryFn: () => fetchSources(range),
    ...QUERY_OPTIONS,
  });

  const sortedVendors = useMemo(
    () =>
      q.data ? [...q.data.vendor_leaderboard].sort((a, b) => b.first_mover_count - a.first_mover_count) : [],
    [q.data],
  );

  return (
    <section className="trends-tab-content trends-sources">
      <TabStatus
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        data={q.data}
        emptyHint="Vendor first-mover stats need Document.organization metadata on REFERENCED_IN edges. Run the trends_snapshot Dagster asset once Neo4j is loaded."
      >
        {q.data && (
          <>
            <BuiltAt at={q.data.built_at} />
            <div>
              <h2 className="trends-section-title">
                Vendors ranked by first-mover count
              </h2>
              <Leaderboard
                rows={sortedVendors}
                columns={vendorColumns}
                rowKey={(row) => row.organization}
              />
            </div>
            <div>
              <h2 className="trends-section-title">
                Echo-lag distribution (first → second org)
              </h2>
              <EchoLagChart buckets={q.data.echo_lag} />
            </div>
          </>
        )}
      </TabStatus>
    </section>
  );
}
